import moment from 'moment';
import type { ReactNode } from 'react';
import { CandidateDetails, CandidateTopper } from '@tempmee/react-blocks';
import type { IAuthorization } from '~/domain/Credentials/types/Credentials';
import { UserProfession } from '~/enums/UserProfession';
import { hasSpecialtyMismatch, useCandidate } from '~/hooks/useCandidate';
import { useSelectedOffice } from '~/hooks/useSelectedOffice';
import { IJob, IMultipleCounters } from '~/models';
import { resolveProfession } from '~/utils/shiftCard';
import { ShiftDetailsPanel } from '../../ShiftDetailsPanel';
import { toCandidateBadges, toShiftCount } from '../../shared/utils/candidateBadges';
import { toCandidateCardData } from '../../shared/utils/candidateCardProps';
import {
  toCandidateCredentials,
  toCandidateFields,
  toProcedureGroups,
  toSpecialtyChips,
} from '../../shared/utils/candidateProfileProps';
import { SpecialtyMismatchAlert } from './SpecialtyMismatchAlert';

/**
 * Keeps the card from collapsing to the height of whichever tab is open.
 *
 * The three tabs carry very different amounts of content — Credentials is a few
 * rows, and Procedures is empty for a professional whose procedures the backend
 * has not filed — so without a floor the panel visibly jumps as the office
 * switches tabs, and an empty Procedures tab reads as a broken page rather than
 * as "nothing recorded".
 *
 * 320px rather than the Figma card's ~660px because dental-portal has no
 * Tailwind build: only the arbitrary values the design system's own stylesheet
 * already emits resolve, and `min-h-[320px]` is the tallest of those that is not
 * `min-h-[960px]`. It clears the collapse without pinning a page-tall card.
 */
const CARD_MIN_HEIGHT = 'min-h-[320px]';

export interface ISingleCandidateViewProps {
  job: Partial<IJob> | null | undefined;
  /** The counter offer being decided on. */
  counter: IMultipleCounters;
  /** Verified credentials for the Credentials tab. */
  authorizations?: IAuthorization[] | null;
  /** The page's single alert — the resolved `ShiftAlertSlot`, already narrowed to one. */
  alert?: ReactNode;
  /** Confirm / Decline controls, supplied by the page that owns the mutations. */
  actions?: ReactNode;
  /**
   * Stacked layout. Only `CandidateDetails` needs telling — the topper answers
   * to its own width.
   */
  isCollapsed?: boolean;
}

/**
 * One candidate's page: who they are, what they are asking for, and everything
 * an office reads before deciding.
 *
 * It is a **single** card — the header, the counter offer and the profile all sit
 * on one surface, as the Figma draws them. Splitting the header onto its own card
 * put a gap through the middle of what the design treats as one object.
 *
 * The `CandidateDetails` block owns the tab switcher and every panel, including
 * the SPECIALTIES section: its `specialtiesAlert` slot takes the qualification
 * warning under that heading, so this view supplies the copy and nothing else —
 * see {@link SpecialtyMismatchAlert}.
 */
export const SingleCandidateView = ({
  job,
  counter,
  authorizations,
  alert,
  actions,
  isCollapsed = false,
}: ISingleCandidateViewProps) => {
  const candidate = counter?.candidate;
  const office = useSelectedOffice(job?.offer_owner?.id);
  const {
    insuranceMismatch,
    licenseNumber,
    licenseState,
    issueDate,
    nationalProviderIdentifier,
    shouldShowNpi,
    yearsOfExperience,
  } = useCandidate(office, candidate ?? null, (job ?? null) as IJob | null);

  const cardData = toCandidateCardData({ counter, job });

  /**
   * Only the page's Confirm / Decline — the way back is the page header's arrow.
   * The block takes a list and places it: above `@md` beside the name, below it
   * stacked into its own row, which is what keeps the name from truncating away
   * at ~450px.
   */
  const topperActions = actions ? [actions] : [];

  const fields = toCandidateFields({
    licenseNumber,
    issuedYear: issueDate ? moment(issueDate).format('YYYY') : undefined,
    licenseState,
    nationalProviderIdentifier,
    shouldShowNpi,
  });

  return (
    <ShiftDetailsPanel
      className={CARD_MIN_HEIGHT}
      data-testid="single-candidate-panel"
      data-cy="single-candidate-panel"
    >
      <CandidateTopper
        {...cardData}
        roleCode={resolveProfession(job?.professionName, job?.subProfession)}
        actions={topperActions}
        testID="candidate-topper"
        actionsTestID="candidate-topper-actions"
        data-cy="candidate-topper"
      />

      {alert}

      <CandidateDetails
        layout={isCollapsed ? 'mobile' : 'desktop'}
        shiftCount={toShiftCount(candidate)}
        experience={yearsOfExperience}
        rating={candidate?.average_rating ?? 0}
        badges={toCandidateBadges(candidate)}
        specialties={toSpecialtyChips(candidate, job)}
        specialtiesAlert={
          <SpecialtyMismatchAlert
            jobSpecialty={job?.specialty}
            // `hasSpecialtyMismatch`, not the hook's `specialtyMatch`: the latter
            // is also false on a shift that names no specialty, and there is no
            // requirement to warn about there.
            specialtyMatch={!hasSpecialtyMismatch(candidate, job)}
            insuranceMismatch={Boolean(insuranceMismatch)}
            isDentistShift={job?.profession === UserProfession.DN}
          />
        }
        credentials={toCandidateCredentials(authorizations)}
        fields={fields}
        procedures={toProcedureGroups(candidate, job)}
        data-testid="candidate-details"
      />
    </ShiftDetailsPanel>
  );
};
